import React, { useState } from 'react';
import { formatUtcToTz } from '../../utils/timezone';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { Card, Text, Button, BlockStack, InlineStack, Pagination, TextField, Icon } from '@shopify/polaris';
import { RefreshIcon, DeleteIcon, SearchIcon } from '@shopify/polaris-icons';
import { FlowStatusPanel } from './FlowStatusPanel';
import { formatServiceLabel } from '../../utils/serviceLabels';

const MAX_PAGES = 5;

export function MessageQueue({ 
  logs, 
  errors, 
  isLoading, 
  onRefresh, 
  onClear,
  service,
  pagination,
  errorPagination,
  onPageChange,
  onErrorPageChange,
  searchValue,
  isSearchActive,
  onSearchChange,
  onSearchSubmit,
  onSearchClear
}) {
  const [selectedTab, setSelectedTab] = useState(0);
  const { currentWorkspace } = useWorkspace();
  const tz = currentWorkspace?.config?.timezone || 'America/New_York';
  const serviceLabel = formatServiceLabel(service);

  // Debug: Log the props received
  // console.log('🔍 MessageQueue props:', {
  //   logs,
  //   errors,
  //   isLoading,
  //   service,
  //   pagination,
  //   errorPagination,
  //   logsLength: logs?.length,
  //   errorsLength: errors?.length
  // });

  const formatDate = (dateString) => formatUtcToTz(dateString, tz);

  const renderPagination = (pageInfo, onChange, label) => {
    if (!pageInfo) return null;
    const totalPages = isSearchActive
      ? (pageInfo.totalPages || 1)
      : Math.min(MAX_PAGES, pageInfo.totalPages || 1);
    const safePage = Math.min(pageInfo.currentPage || 1, totalPages);
    if (totalPages <= 1) return null;
    return (
      <InlineStack align="space-between" blockAlign="center">
        <Text variant="bodySm" tone="subdued">
          Showing page {safePage} of {totalPages}, 10 records per page
        </Text>
        <Pagination
          label={`${label} page ${safePage} of ${totalPages}`}
          hasPrevious={safePage > 1}
          onPrevious={() => onChange && onChange(Math.max(1, safePage - 1))}
          hasNext={safePage < totalPages}
          onNext={() => onChange && onChange(Math.min(totalPages, safePage + 1))}
        />
      </InlineStack>
    );
  };

  const renderLogsTable = (logData, showLevel = true) => {
    if (!logData || logData.length === 0) {
      return (
        <div className="no-logs">
          <Text variant="bodyMd" color="subdued">
            {isSearchActive ? 'No matching messages found' : 'No logs available'}
          </Text>
        </div>
      );
    }

    return (
      <div className="logs-table">
        <div className="logs-header">
          <div className="log-cell">Date/Time</div>
          {showLevel && <div className="log-cell">Level</div>}
          <div className="log-cell">Message & Details</div>
        </div>
        
        {logData.map((log, index) => (
          <div key={log.id || index} className="log-row">
            <div className="log-cell">
              <Text variant="bodySm">
                {formatDate(log.updatedAt || log.createdAt || log.timestamp || log.date)}
              </Text>
            </div>
            {showLevel && (
              <div className="log-cell">
                <Text variant="bodySm" color="subdued">
                  {log.level || 'INFO'}
                </Text>
              </div>
            )}
            <div className="log-cell log-cell-message">
              <Text variant="bodyMd">
                {log.message || log.msg || 'No message'}
              </Text>
              {log.details && (
                <div>
                  <Text variant="bodySm" color="subdued">
                    {log.details}
                  </Text>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card>
      <BlockStack gap="loose">
        <div className="card-title-container">
          <Text variant="headingMd" fontWeight="bold" className="card-title-standard">
            Message Queue - {serviceLabel}
          </Text>
          <InlineStack gap="base">
            <Button
              icon={RefreshIcon}
              variant="tertiary"
              onClick={onRefresh}
              disabled={isLoading}
            >
              Refresh
            </Button>
            <Button
              icon={DeleteIcon}
              variant="tertiary"
              onClick={onClear}
              disabled={isLoading || (!logs?.length && !errors?.length)}
            >
              Clear Logs
            </Button>
          </InlineStack>
        </div>

        {isLoading && (
          <div className="loading-indicator">
            <Text variant="bodyMd">Loading logs...</Text>
          </div>
        )}

        {/* Simple tab buttons */}
        <div className="tab-buttons">
          <Button
            className="tab-button"
            variant={selectedTab === 0 ? 'primary' : 'secondary'}
            onClick={() => setSelectedTab(0)}
          >
            All Logs
          </Button>
          <Button
            className="tab-button"
            variant={selectedTab === 1 ? 'primary' : 'secondary'}
            onClick={() => setSelectedTab(1)}
          >
            Error Logs
          </Button>
          <Button
            className="tab-button"
            variant={selectedTab === 2 ? 'primary' : 'secondary'}
            onClick={() => setSelectedTab(2)}
          >
            Retries
          </Button>
        </div>

        {selectedTab !== 2 && (
          <div
            className="message-queue-search"
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                onSearchSubmit();
              }
            }}
          >
            <TextField
              label="Search message queue"
              labelHidden
              value={searchValue}
              onChange={onSearchChange}
              prefix={<Icon source={SearchIcon} tone="subdued" />}
              suffix={searchValue ? (
                <Button variant="plain" onClick={onSearchClear} accessibilityLabel="Clear message queue search">
                  Clear
                </Button>
              ) : null}
              placeholder="Search by order number, PO number, execution ID, or message"
              autoComplete="off"
              maxLength={100}
            />
          </div>
        )}

        <div>
          {selectedTab === 0 && (
            <BlockStack gap="loose">
              <Text variant="bodySm" color="subdued">
                {isSearchActive
                  ? `Found ${pagination?.totalLogs || 0} matching log records, 10 per page.`
                  : 'Showing latest 50 log records, 10 per page.'}
              </Text>
              {renderLogsTable(logs, true)}
              {renderPagination(pagination, onPageChange, 'Logs')}
            </BlockStack>
          )}
          {selectedTab === 1 && (
            <BlockStack gap="loose">
              <Text variant="bodySm" color="subdued">
                {isSearchActive
                  ? `Found ${errorPagination?.totalLogs || 0} matching error log records, 10 per page.`
                  : 'Showing latest 50 error log records, 10 per page.'}
              </Text>
              {renderLogsTable(errors, true)}
              {renderPagination(errorPagination, onErrorPageChange, 'Errors')}
            </BlockStack>
          )}
          {selectedTab === 2 && <FlowStatusPanel service={service} embedded />}
        </div>
      </BlockStack>
    </Card>
  );
}
